// The Daily Racket: the city's weekly front page, written automatically every Monday.
import { useState } from 'react';
import { api } from '../api.ts';
import { useGame, duration } from '../state.tsx';
import { Loading, useLoad, PlayerLink } from '../components/ui.tsx';
import { Portrait } from '../art/Portrait.tsx';

const dateFmt = (t: number) => new Date(t).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
const shortDate = (t: number) => new Date(t).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

/** Turns the names in a story into links. */
function StoryText({ s }: { s: any }) {
  let parts: any[] = [s.text];
  const link = (name: string | null, id: number | null) => {
    if (!name || !id) return;
    parts = parts.flatMap((p) => (typeof p === 'string' ? p.split(name).flatMap((x, i, arr) => (i < arr.length - 1 ? [x, <PlayerLink key={`${id}-${i}-${Math.random()}`} id={id} name={name} />] : [x])) : [p]));
  };
  link(s.playerName, s.playerId);
  link(s.otherName ?? null, s.otherId ?? null);
  return <>{parts}</>;
}

function Paper({ issue, preview }: { issue: any; preview?: boolean }) {
  return (
    <div className="newspaper">
      <div className="np-masthead">
        <div className="np-ears"><span>{preview ? 'EARLY EDITION' : 'WEEKLY EDITION'}</span><span>PRICE: ONE FAVOR</span></div>
        <div className="np-title">The Daily Racket</div>
        <div className="np-dateline">Week of {dateFmt(issue.weekStart)}{preview ? ' · so far' : ''} · “All the news that’s fit to whisper”</div>
      </div>
      {issue.lead ? (
        <div className="np-lead">
          <div className="np-headline">{issue.lead.headline}</div>
          <div className="np-lead-body">
            {issue.lead.playerId && issue.lead.portrait !== undefined && <Portrait seed={issue.lead.portrait} playerId={issue.lead.playerId} size={84} />}
            <p><StoryText s={issue.lead} /></p>
          </div>
        </div>
      ) : <div className="np-lead"><div className="np-headline">QUIET WEEK IN THE CITY</div><p>Not a peep from the underworld. Suspicious, if you ask us.</p></div>}
      <div className="np-columns">
        {issue.stories.map((s: any) => (
          <div key={s.key} className="np-story">
            <div className="np-subhead">{s.headline}</div>
            <p><StoryText s={s} /></p>
          </div>
        ))}
      </div>
      <div className="np-numbers">
        <div className="np-subhead">THE WEEK IN NUMBERS</div>
        <div className="np-number-row">{issue.numbers.map((n: any) => <div key={n.label}><b>{n.value}</b><span>{n.label}</span></div>)}</div>
      </div>
    </div>
  );
}

export function RacketPage() {
  const { me } = useGame();
  const [week, setWeek] = useState<number | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const { data } = useLoad(() => api.get(`/api/racket${week ? `?week=${week}` : ''}`), [week]);
  if (!data || !me) return <Loading />;
  return (
    <div>
      <div className="row" style={{ alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
        <span className="small muted grow">A new front page comes out every Monday. Next edition in {duration(Math.max(0, data.nextEdition - Date.now()))}.</span>
        {data.archive.length > 0 && (
          <select value={week ?? data.issue?.weekStart ?? ''} onChange={(e) => { setShowPreview(false); setWeek(Number(e.target.value)); }}>
            {data.archive.map((w: number) => <option key={w} value={w}>Week of {shortDate(w)}</option>)}
          </select>
        )}
        <button className="btn small gray" onClick={() => setShowPreview(!showPreview)}>{showPreview ? 'Back to the paper' : 'This week so far'}</button>
      </div>
      {showPreview ? <Paper issue={data.preview} preview />
        : data.issue ? <Paper issue={data.issue} />
        : (
          <div>
            <div className="flash good"><div>The first edition of The Daily Racket comes out on Monday. Here’s a sneak peek at what the reporters have so far.</div></div>
            <Paper issue={data.preview} preview />
          </div>
        )}
    </div>
  );
}
